import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, MapPin, CheckCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { ordersAPI } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import toast from 'react-hot-toast';

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const { t } = useSettings();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({ street: '', city: '', state: '', zipCode: '', country: 'Tunisia' });
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  // Discount

  const shipping = total > 500 ? 0 : 15;
  const tax = parseFloat((total * 0.19).toFixed(2));
  const discountAmount = 0;
  const grandTotal = Math.max(0, total + shipping + tax - discountAmount);


  const handlePlaceOrder = async () => {
    if (!address.street || !address.city || !address.zipCode) return toast.error('Please fill all address fields');
    setLoading(true);
    try {
      const orderItems = items.map(i => ({ product: i._id, quantity: i.quantity }));
      const res = await ordersAPI.create({
        items: orderItems, shippingAddress: address, paymentMethod,
      });
      clearCart();
      toast.success('Order placed successfully!');
      navigate(`/orders/${res.data.order._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error placing order');
    }
    setLoading(false);
  };

  if (items.length === 0) { navigate('/cart'); return null; }

  const STEPS = [t('checkout','shipping_addr'), t('checkout','payment'), t('checkout','review')];

  return (
    <div className="container" style={{ padding: '40px 28px' }}>
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 700, marginBottom: 32, letterSpacing: '-0.03em' }}>{t('checkout','title')}</h1>

      {/* Progress Steps */}
      <div className="checkout-steps" style={{ display: 'flex', alignItems: 'center', marginBottom: 36 }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none' }}>
            <div onClick={() => step > i + 1 && setStep(i + 1)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', background: step === i+1 ? 'var(--accent)' : step > i+1 ? 'var(--success)' : 'var(--bg-card)', borderRadius: 14, cursor: step > i+1 ? 'pointer' : 'default', color: step >= i+1 ? 'white' : 'var(--text-muted)', border: `1.5px solid ${step === i+1 ? 'var(--accent)' : step > i+1 ? 'var(--success)' : 'var(--border)'}`, transition: 'var(--transition)', whiteSpace: 'nowrap' }}>
              <span className="checkout-step-number" style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                {step > i + 1 ? '✓' : i + 1}
              </span>
              <span className="checkout-step-label" style={{ fontWeight: 700, fontSize: 14 }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: step > i+1 ? 'var(--success)' : 'var(--border)', margin: '0 8px', borderRadius: 99 }} />}
          </div>
        ))}
      </div>

      <div className="checkout-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 28 }}>
        {/* Left */}
        <div>
          {/* Step 1: Shipping */}
          {step === 1 && (
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                <MapPin size={20} color="var(--accent)" />{t('checkout','shipping_addr')}
              </h3>
              <div className="checkout-address-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">{t('checkout','street')} *</label>
                  <input className="form-input" placeholder="123 Main Street" value={address.street} onChange={e => setAddress(p => ({ ...p, street: e.target.value }))} />
                </div>
                {[['city',t('checkout','city'),'Sfax'],['state',t('checkout','state'),'Sfax Governorate'],['zipCode',t('checkout','zip'),'3000'],['country',t('checkout','country'),'Tunisia']].map(([key, label, ph]) => (
                  <div key={key} className="form-group">
                    <label className="form-label">{label}</label>
                    <input className="form-input" placeholder={ph} value={address[key]} onChange={e => setAddress(p => ({ ...p, [key]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <button className="btn btn-primary btn-lg" onClick={() => { if (!address.street || !address.city) { toast.error('Fill required fields'); return; } setStep(2); }}>
                {t('checkout','continue_payment')}
              </button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                <CreditCard size={20} color="var(--accent)" />{t('checkout','payment')}
              </h3>
              {[['cash_on_delivery','💵',t('checkout','cash'),t('checkout','cash_sub')],['stripe','💳',t('checkout','card'),t('checkout','card_sub')]].map(([value,icon,label,desc]) => (
                <div key={value} onClick={() => setPaymentMethod(value)}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 20, background: paymentMethod === value ? 'var(--accent-dim)' : 'var(--bg-secondary)', border: `2px solid ${paymentMethod === value ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 16, cursor: 'pointer', marginBottom: 12, transition: 'var(--transition)' }}>
                  <span style={{ fontSize: 28 }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{label}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{desc}</div>
                  </div>
                  {paymentMethod === value && <CheckCircle size={20} color="var(--accent)" />}
                </div>
              ))}

              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button className="btn btn-secondary" onClick={() => setStep(1)}>{t('checkout','back')}</button>
                <button className="btn btn-primary btn-lg" onClick={() => setStep(3)}>Review Order</button>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20 }}>Review Your Order</h3>
              {items.map(item => (
                <div key={item._id} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <img src={item.thumbnail} alt={item.name} style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', background: 'var(--bg-secondary)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Qty: {item.quantity}</div>
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--gold)' }}>${(item.price * item.quantity).toLocaleString()}</div>
                </div>
              ))}
              <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
                <div>📍 {address.street}, {address.city}, {address.country}</div>
                <div style={{ marginTop: 4 }}>💳 {paymentMethod === 'cash_on_delivery' ? 'Cash on Delivery' : 'Credit Card'}</div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button className="btn btn-secondary" onClick={() => setStep(2)}>{t('checkout','back')}</button>
                <button className="btn btn-gold btn-lg" style={{ flex: 1 }} onClick={handlePlaceOrder} disabled={loading}>
                  {loading ? 'Placing Order...' : `${t('checkout','place_order')} — $${grandTotal.toFixed(2)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Summary */}
        <div>
          <div className="card checkout-summary-sticky" style={{ position: 'sticky', top: 90 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 20 }}>{t('cart','summary')}</h3>
            {items.slice(0, 3).map(item => (
              <div key={item._id} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                <img src={item.thumbnail} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                <div style={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>${(item.price * item.quantity).toLocaleString()}</div>
              </div>
            ))}
            {items.length > 3 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>+{items.length - 3} more items</div>}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              {[
                [t('cart','subtotal'), `$${total.toLocaleString()}`],
                [t('cart','shipping'), shipping === 0 ? t('cart','free') : `$${shipping}`],
                [t('cart','tax'), `$${tax}`],
                ...(discountAmount > 0 ? [['Discount', `-$${discountAmount.toFixed(2)}`]] : []),
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14, color: 'var(--text-secondary)' }}>
                  <span>{label}</span>
                  <span style={{ color: val === t('cart','free') ? 'var(--success)' : label === 'Discount' ? 'var(--success)' : 'var(--text-primary)', fontWeight: label === 'Discount' ? 700 : 400 }}>{val}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, borderTop: '1.5px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <span>{t('cart','total')}</span>
                <span style={{ color: 'var(--gold)' }}>${grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}